/**
 * Step 6 Mobile: Spending
 * 1-5 spending phases as stacked cards (#1538), each with add/remove and an
 * in-card itemized breakdown per category. Desktop uses a table+drawer; on
 * mobile the existing stacked-card pattern already used across this step
 * (and Step3Holdings.mobile's card list) is a better fit than a drawer.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Step6FormData, type SpendingPhase, type ExpenseLineItem, type AllStepFormData, Step6Schema } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';
import {
  CATEGORY_ORDER,
  CATEGORY_META,
  SpendingCategoryKey,
  getCategoryItems,
  getCategoryTotal,
  phaseHasSpendingData,
  createDefaultSpendingPhases,
} from '../../utils/spendingPhaseHelpers';

const MIN_SPENDING_PHASES = 1;
const MAX_SPENDING_PHASES = 5;

interface Step5Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step6FormData) => void;
  onSave: (data: Step6FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function Step5SpendingMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step5Props>) {
  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<Step6FormData>({
    resolver: zodResolver(Step6Schema),
    defaultValues: {
      spendingPhases: initialData?.spendingPhases?.map((p: SpendingPhase) => ({ ...p, inflationLinked: p.inflationLinked ?? true })) || createDefaultSpendingPhases(),
      oneTimeExpenses: initialData?.oneTimeExpenses || [],
      recurringExpenses: initialData?.recurringExpenses || [],
    },
  });

  const [copySourceByPhase, setCopySourceByPhase] = useState<Record<number, string>>({});
  const [pendingCopy, setPendingCopy] = useState<{ phaseIndex: number; sourceId: string } | null>(null);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState<number | null>(null);
  const [confirmBreakdown, setConfirmBreakdown] = useState<{ phaseIndex: number; key: SpendingCategoryKey } | null>(null);

  const handleBlur = () => onSave(watch());

  const phases = watch('spendingPhases') || [];

  function syncFlatTotal(phaseIndex: number, key: SpendingCategoryKey) {
    const meta = CATEGORY_META[key];
    const items = (getValues(`spendingPhases.${phaseIndex}.${meta.itemsField}`) as ExpenseLineItem[]) || [];
    const sum = items.reduce((s: number, i: ExpenseLineItem) => s + (Number(i.annualAmount) || 0), 0);
    setValue(`spendingPhases.${phaseIndex}.${key}`, sum, { shouldValidate: true });
  }

  function handleAddPhase() {
    const current = (getValues('spendingPhases') as SpendingPhase[]) || [];
    if (current.length >= MAX_SPENDING_PHASES) return;
    const lastIndex = current.length - 1;
    const last = current[lastIndex];
    let lastEndYear = last?.endYear;
    if (last && lastEndYear == null) {
      lastEndYear = last.startYear + 9;
      setValue(`spendingPhases.${lastIndex}.endYear`, lastEndYear, { shouldValidate: true });
    }
    const newStart = (lastEndYear ?? 0) + 1;
    const next = [...current];
    next.push({
      phaseId: crypto.randomUUID(),
      label: 'New Phase',
      startYear: newStart,
      endYear: undefined,
      livingExpenses: 0,
      travelLeisure: 0,
      otherDiscretionary: 0,
      inflationLinked: true,
    });
    setValue('spendingPhases', next, { shouldValidate: true });
    setTimeout(handleBlur, 0);
  }

  function handleRemovePhase(index: number) {
    if (confirmRemoveIndex !== index) {
      setConfirmRemoveIndex(index);
      setTimeout(() => setConfirmRemoveIndex((prev) => (prev === index ? null : prev)), 3000);
      return;
    }
    const current = (getValues('spendingPhases') as SpendingPhase[]) || [];
    if (current.length <= MIN_SPENDING_PHASES) return;
    const next = current.filter((_, i) => i !== index);
    setValue('spendingPhases', next, { shouldValidate: true });
    setConfirmRemoveIndex(null);
    setTimeout(handleBlur, 0);
  }

  function handleBreakdownClick(phaseIndex: number, key: SpendingCategoryKey) {
    const isConfirming = confirmBreakdown?.phaseIndex === phaseIndex && confirmBreakdown.key === key;
    if (!isConfirming) {
      setConfirmBreakdown({ phaseIndex, key });
      setTimeout(() => setConfirmBreakdown((prev) => (prev?.phaseIndex === phaseIndex && prev.key === key ? null : prev)), 3000);
      return;
    }
    const meta = CATEGORY_META[key];
    const seedAmount = Number(getValues(`spendingPhases.${phaseIndex}.${key}`)) || 0;
    setValue(`spendingPhases.${phaseIndex}.${meta.itemsField}`, seedAmount > 0 ? [{ itemId: crypto.randomUUID(), description: '', annualAmount: seedAmount }] : [], { shouldValidate: true });
    setConfirmBreakdown(null);
    handleBlur();
  }

  function handleAddItem(phaseIndex: number, key: SpendingCategoryKey) {
    const meta = CATEGORY_META[key];
    const current = (getValues(`spendingPhases.${phaseIndex}.${meta.itemsField}`) as ExpenseLineItem[]) || [];
    setValue(`spendingPhases.${phaseIndex}.${meta.itemsField}`, [...current, { itemId: crypto.randomUUID(), description: '', annualAmount: 0 }], { shouldValidate: true });
    setTimeout(handleBlur, 0);
  }

  function handleRemoveItem(phaseIndex: number, key: SpendingCategoryKey, itemIndex: number) {
    const meta = CATEGORY_META[key];
    const current = (getValues(`spendingPhases.${phaseIndex}.${meta.itemsField}`) as ExpenseLineItem[]) || [];
    setValue(`spendingPhases.${phaseIndex}.${meta.itemsField}`, current.filter((_, i) => i !== itemIndex), { shouldValidate: true });
    syncFlatTotal(phaseIndex, key);
    setTimeout(handleBlur, 0);
  }

  function performCopy(phaseIndex: number, source: SpendingPhase) {
    CATEGORY_ORDER.forEach((key) => {
      const meta = CATEGORY_META[key];
      const items = getCategoryItems(source, key);
      setValue(`spendingPhases.${phaseIndex}.${key}`, getCategoryTotal(source, key), { shouldValidate: true });
      setValue(
        `spendingPhases.${phaseIndex}.${meta.itemsField}`,
        items ? items.map((i) => ({ ...i, itemId: crypto.randomUUID() })) : undefined,
        { shouldValidate: true }
      );
    });
    setPendingCopy(null);
    setCopySourceByPhase((prev) => ({ ...prev, [phaseIndex]: '' }));
    handleBlur();
  }

  function handleCopyCommit(phaseIndex: number) {
    const sourceId = copySourceByPhase[phaseIndex];
    const source = phases.find((p: SpendingPhase) => p.phaseId === sourceId);
    if (!source) return;
    if (phaseHasSpendingData(phases[phaseIndex])) {
      setPendingCopy({ phaseIndex, sourceId });
    } else {
      performCopy(phaseIndex, source);
    }
  }

  return (
    <WizardMobileShell step={6} totalSteps={10} title="Spending" onBack={onBack} onContinue={() => handleSubmit(onNext)()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <p className="mobile-step-desc">Up to {MAX_SPENDING_PHASES} spending phases — tap a category to break it down into line items.</p>
        <p className="mobile-hint" style={{ marginBottom: 8 }}>
          Year 1 = your first year of retirement. Year 10 = ten years after retirement.
        </p>
        {errors.spendingPhases && !Array.isArray(errors.spendingPhases) && (errors.spendingPhases as { message?: string }).message && (
          <div className="auth-mobile__error">{(errors.spendingPhases as { message?: string }).message}</div>
        )}
        {phases?.map((p: SpendingPhase, i: number) => {
          const isLast = i === phases.length - 1;
          const otherPhases = phases.filter((_: SpendingPhase, idx: number) => idx !== i);
          return (
            <div key={p.phaseId || i} className="mobile-person-card">
              <h4 className="mobile-person-card__title">{p?.label || `Phase ${i + 1}`}{p?.startYear ? ` (yr ${p.startYear}${p?.endYear ? `–${p.endYear}` : ' onwards'})` : ''}</h4>

              <label className="mobile-label">Phase name</label>
              <input type="text" className="mobile-input" {...register(`spendingPhases.${i}.label` as Path<Step6FormData>)} onBlur={handleBlur} />

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label className="mobile-label">Start (retirement year)</label>
                  <input type="number" className="mobile-input" min={1}
                    {...register(`spendingPhases.${i}.startYear` as Path<Step6FormData>, { valueAsNumber: true })}
                    onBlur={handleBlur}
                    disabled={i > 0}
                  />
                  {(errors.spendingPhases as Array<{ startYear?: { message?: string } } | undefined> | undefined)?.[i]?.startYear && (
                    <span className="mobile-error">{(errors.spendingPhases as Array<{ startYear?: { message?: string } }>)[i].startYear?.message}</span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="mobile-label">End (retirement year)</label>
                  <input type="number" className="mobile-input" min={1}
                    {...register(`spendingPhases.${i}.endYear` as Path<Step6FormData>, {
                      valueAsNumber: true,
                      onChange: (e: { target: { value: string } }) => {
                        if (!isLast) {
                          const newEnd = Number(e.target.value);
                          if (Number.isFinite(newEnd)) setValue(`spendingPhases.${i + 1}.startYear`, newEnd + 1, { shouldValidate: true });
                        }
                      },
                    })}
                    onBlur={handleBlur}
                    placeholder={isLast ? 'Until end of projection' : undefined}
                  />
                  {(errors.spendingPhases as Array<{ endYear?: { message?: string } } | undefined> | undefined)?.[i]?.endYear && (
                    <span className="mobile-error">{(errors.spendingPhases as Array<{ endYear?: { message?: string } }>)[i].endYear?.message}</span>
                  )}
                </div>
              </div>

              {CATEGORY_ORDER.map((key) => {
                const meta = CATEGORY_META[key];
                const items = getCategoryItems(p, key);
                const isItemized = items != null;
                const isConfirmingBreakdown = confirmBreakdown?.phaseIndex === i && confirmBreakdown.key === key;
                return (
                  <div key={key} className="mobile-spending-category">
                    <label className="mobile-label">{meta.label} (£/year)</label>
                    {isItemized ? (
                      <div className="mobile-spending-items">
                        {(items || []).map((item, itemIndex) => (
                          <div key={item.itemId} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            <input
                              type="text"
                              className="mobile-input"
                              placeholder="e.g. Council tax"
                              {...register(`spendingPhases.${i}.${meta.itemsField}.${itemIndex}.description`)}
                              onBlur={handleBlur}
                            />
                            <input
                              type="number"
                              className="mobile-input"
                              placeholder="£/yr"
                              {...register(`spendingPhases.${i}.${meta.itemsField}.${itemIndex}.annualAmount`, {
                                valueAsNumber: true,
                                onChange: () => syncFlatTotal(i, key),
                              })}
                              onBlur={handleBlur}
                            />
                            <button type="button" className="mobile-btn" onClick={() => handleRemoveItem(i, key, itemIndex)} aria-label="Remove line item">&#128465;</button>
                          </div>
                        ))}
                        <button type="button" className="mobile-btn mobile-btn--full" onClick={() => handleAddItem(i, key)}>+ Add line item</button>
                        <p className="mobile-hint">= {formatCurrency(getCategoryTotal(p, key))}/year — itemized, always sums from its line items now.</p>
                      </div>
                    ) : (
                      <>
                        <input
                          type="number"
                          className="mobile-input"
                          {...register(`spendingPhases.${i}.${key}` as Path<Step6FormData>, { valueAsNumber: true })}
                          onBlur={handleBlur}
                        />
                        <button type="button" className="mobile-btn" onClick={() => handleBreakdownClick(i, key)}>
                          {isConfirmingBreakdown ? "Tap again — can't be undone" : 'Break this down into line items'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}

              {otherPhases.length > 0 && (
                <div className="mobile-spending-copy">
                  <label className="mobile-label">Copy spending from</label>
                  {pendingCopy?.phaseIndex === i ? (
                    <div className="mobile-spending-copy-warning">
                      <p>This phase already has spending entered. Replace it with {phases.find((ph: SpendingPhase) => ph.phaseId === pendingCopy.sourceId)?.label}?</p>
                      <button type="button" className="mobile-btn" onClick={() => setPendingCopy(null)}>Cancel</button>
                      <button
                        type="button"
                        className="mobile-btn mobile-btn--primary"
                        onClick={() => {
                          const source = phases.find((ph: SpendingPhase) => ph.phaseId === pendingCopy.sourceId);
                          if (source) performCopy(i, source);
                        }}
                      >
                        Replace data
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select
                        className="mobile-input"
                        value={copySourceByPhase[i] || ''}
                        onChange={(e) => setCopySourceByPhase((prev) => ({ ...prev, [i]: e.target.value }))}
                      >
                        <option value="">Choose a phase&hellip;</option>
                        {otherPhases.map((ph: SpendingPhase) => (
                          <option key={ph.phaseId} value={ph.phaseId}>{ph.label || 'Untitled phase'}</option>
                        ))}
                      </select>
                      <button type="button" className="mobile-btn" disabled={!copySourceByPhase[i]} onClick={() => handleCopyCommit(i)}>Copy</button>
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                className="mobile-btn mobile-btn--full"
                disabled={phases.length <= MIN_SPENDING_PHASES}
                onClick={() => handleRemovePhase(i)}
              >
                {confirmRemoveIndex === i ? 'Tap again to confirm' : 'Remove this phase'}
              </button>
            </div>
          );
        })}

        <button
          type="button"
          className="mobile-btn mobile-btn--primary mobile-btn--full"
          disabled={phases.length >= MAX_SPENDING_PHASES}
          onClick={handleAddPhase}
        >
          + Add Phase
        </button>
      </div>
    </WizardMobileShell>
  );
}
